/**
 * Capture a rendered viz as a PNG data URL, for the "Save as Cognos
 * report" flow — the saved report embeds this image so a Cognos viewer
 * sees the chart the user actually built, not just the raw table.
 *
 * <p>The three viz renderers produce different DOM:
 * <ul>
 *   <li><b>ECharts</b> and <b>deck.gl</b> render into a {@code <canvas>}
 *       — {@code canvas.toDataURL()} gives the PNG directly.</li>
 *   <li><b>vega-embed</b> renders an {@code <svg>} by default — we
 *       serialize it, draw it onto an off-screen canvas, and export
 *       that.</li>
 * </ul>
 *
 * <p>Every failure path returns {@code null} rather than throwing: a
 * missing capture just means the saved report falls back to table-only,
 * which is the pre-existing behaviour — not an error worth aborting the
 * save for.
 *
 * <p>deck.gl note: a WebGL canvas only yields non-blank pixels from
 * {@code toDataURL()} when the context was created with
 * {@code preserveDrawingBuffer: true} — see {@code DeckGlRenderer}.
 */
export async function captureVizImage(el: HTMLElement | null): Promise<string | null> {
  if (!el) {
    console.warn("[captureVizImage] no element ref — table-only save");
    return null;
  }

  // Wait two animation frames before capture so any in-flight render /
  // animation has had a chance to paint. Cheap (~32ms) and resolves the
  // common race where the user clicks Save the instant the chart appears.
  await new Promise<void>((r) => requestAnimationFrame(() => requestAnimationFrame(() => r())));

  // vega-embed AND ECharts (now configured in SVG renderer mode for
  // reliable export) both emit an <svg>. Prefer SVG when present.
  //
  // We must NOT use `querySelector("svg")` here — VizChrome's toolbar
  // buttons render lucide-react icons as inline 14-16 px SVGs that
  // come BEFORE the chart in DOM order, so the first match is always
  // a tiny icon. That's exactly what shipped to Cognos in the first
  // capture attempt: a faithful 14×14 PNG of a zoom-in icon. The fix
  // is to enumerate every <svg> in the subtree and pick the LARGEST
  // by bounding-rect area — the chart will always dwarf any icon by
  // an order of magnitude.
  const candidates = Array.from(el.querySelectorAll("svg"));
  let chartSvg: SVGElement | null = null;
  let largestArea = 0;
  for (const s of candidates) {
    if (!(s instanceof SVGElement)) continue;
    const rect = s.getBoundingClientRect();
    const area = rect.width * rect.height;
    if (area > largestArea) {
      largestArea = area;
      chartSvg = s;
    }
  }
  // Require a minimum area so a chart container that genuinely hasn't
  // rendered (all SVGs are icon-sized) falls through to the canvas /
  // null branch rather than shipping a tiny icon as the "chart".
  const MIN_CHART_AREA = 100 * 100;
  if (chartSvg && largestArea >= MIN_CHART_AREA) {
    const png = await svgToPng(chartSvg);
    if (png) return png;
    console.warn("[captureVizImage] largest SVG rasterized to null (area=" + largestArea + ")");
  } else if (chartSvg) {
    console.warn(
      "[captureVizImage] only icon-sized SVGs found (largest area=" + largestArea +
      "); chart likely not rendered yet — skipping to canvas fallback",
    );
  }

  // deck.gl WebGL canvas — created with preserveDrawingBuffer=true so
  // toDataURL is stable on first call. Falls through to null on failure;
  // the saved report then degrades to table-only.
  const canvas = el.querySelector("canvas");
  if (canvas instanceof HTMLCanvasElement) {
    const png = canvasToPng(canvas);
    if (png) return png;
    console.warn("[captureVizImage] canvas present but toDataURL returned null/short");
  }

  console.warn("[captureVizImage] no SVG and no usable canvas — table-only save");
  return null;
}

/** {@code canvas.toDataURL} guarded against tainted canvases + degenerate output. */
function canvasToPng(canvas: HTMLCanvasElement): string | null {
  try {
    const url = canvas.toDataURL("image/png");
    // A blank/degenerate result (e.g. a 0-sized or never-drawn canvas)
    // serialises to a trivially short data URL — don't ship that.
    return url && url.startsWith("data:image/png") && url.length > 256 ? url : null;
  } catch {
    // Tainted canvas (cross-origin paint) etc.
    return null;
  }
}

/** Serialize an <svg>, rasterize it onto a white canvas, export PNG. */
async function svgToPng(svg: SVGElement): Promise<string | null> {
  try {
    const rect = svg.getBoundingClientRect();
    const width = Math.max(1, Math.round(rect.width || 800));
    const height = Math.max(1, Math.round(rect.height || 400));

    const xml = new XMLSerializer().serializeToString(svg);
    const blob = new Blob([xml], { type: "image/svg+xml;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    try {
      const img = await loadImage(url);
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      if (!ctx) return null;
      // White backing — an SVG with no background would otherwise
      // rasterize transparent and look broken on Cognos's white page.
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, width, height);
      ctx.drawImage(img, 0, 0, width, height);
      return canvas.toDataURL("image/png");
    } finally {
      URL.revokeObjectURL(url);
    }
  } catch {
    return null;
  }
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("svg rasterization: image load failed"));
    img.src = src;
  });
}
